import { encodeTime } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { SaveSessionEndpointFieldValueType } from '../../../activity-timeline-actions';
import { ReportTypeField } from '../../../__generated__/ReportTypeField';

type SessionReportFormValuesFieldType =
  | { __typename: 'VOLUNTEER_BooleanFieldType'; value: string }
  | { __typename: 'VOLUNTEER_CheckboxFieldType'; value: boolean }
  | { __typename: 'VOLUNTEER_DropdownFieldType'; value: string[] }
  | { __typename: 'VOLUNTEER_NumericFieldType'; value: string }
  | { __typename: 'VOLUNTEER_RatingFieldType'; value: number | undefined }
  | { __typename: 'VOLUNTEER_TextFieldType'; value: string }
  | { __typename: 'VOLUNTEER_TimeFieldType'; value: Date | '' }
  | { __typename: 'VOLUNTEER_AttachmentFieldType'; value: any };

export function getInitialFieldValueForIndividualReport(
  reportTypeFieldItem: ReportTypeField,
  existingFieldValues?: any[]
): SessionReportFormValuesFieldType {
  const { field } = reportTypeFieldItem;

  if (existingFieldValues) {
    const existing = existingFieldValues.find((fv: any) => fv.field?.fieldId === field.fieldId);
    if (existing) {
      return getValueFromExistingFieldValue(field.__typename, existing);
    }
  }

  return getDefaultFieldValue(field.__typename);
}

function getDefaultFieldValue(typename: string): SessionReportFormValuesFieldType {
  switch (typename) {
    case 'VOLUNTEER_BooleanFieldType':
      return { __typename: 'VOLUNTEER_BooleanFieldType', value: '' };
    case 'VOLUNTEER_CheckboxFieldType':
      return { __typename: 'VOLUNTEER_CheckboxFieldType', value: false };
    case 'VOLUNTEER_DropdownFieldType':
      return { __typename: 'VOLUNTEER_DropdownFieldType', value: [] };
    case 'VOLUNTEER_NumericFieldType':
      return { __typename: 'VOLUNTEER_NumericFieldType', value: '' };
    case 'VOLUNTEER_RatingFieldType':
      return { __typename: 'VOLUNTEER_RatingFieldType', value: undefined };
    case 'VOLUNTEER_TextFieldType':
      return { __typename: 'VOLUNTEER_TextFieldType', value: '' };
    case 'VOLUNTEER_TimeFieldType':
      return { __typename: 'VOLUNTEER_TimeFieldType', value: '' };
    case 'VOLUNTEER_AttachmentFieldType':
      return { __typename: 'VOLUNTEER_AttachmentFieldType', value: { attachments: [], addedAttachments: [], addedAttachmentsBase64: [], attachmentCreationTokens: [], attachmentNames: [], removedAttachmentIds: [] } };
    default:
      return { __typename: 'VOLUNTEER_TextFieldType', value: '' };
  }
}

function getValueFromExistingFieldValue(typename: string, existing: any): SessionReportFormValuesFieldType {
  switch (typename) {
    case 'VOLUNTEER_BooleanFieldType':
      return { __typename: 'VOLUNTEER_BooleanFieldType', value: existing.boolValue != null ? String(existing.boolValue) : '' };
    case 'VOLUNTEER_CheckboxFieldType':
      return { __typename: 'VOLUNTEER_CheckboxFieldType', value: existing.checked ?? false };
    case 'VOLUNTEER_DropdownFieldType':
      return { __typename: 'VOLUNTEER_DropdownFieldType', value: existing.dropdownFieldOptions?.map((o: any) => o.dropdownFieldOptionId) ?? [] };
    case 'VOLUNTEER_NumericFieldType':
      return { __typename: 'VOLUNTEER_NumericFieldType', value: existing.numericValue != null ? String(existing.numericValue) : '' };
    case 'VOLUNTEER_RatingFieldType':
      return { __typename: 'VOLUNTEER_RatingFieldType', value: existing.ratingValue };
    case 'VOLUNTEER_TextFieldType':
      return { __typename: 'VOLUNTEER_TextFieldType', value: existing.textValue ?? '' };
    case 'VOLUNTEER_TimeFieldType':
      return { __typename: 'VOLUNTEER_TimeFieldType', value: '' };
    default:
      return { __typename: 'VOLUNTEER_TextFieldType', value: '' };
  }
}

export function getFieldValuesFromFormValues(values: { fields: Record<string, SessionReportFormValuesFieldType> }): SaveSessionEndpointFieldValueType[] {
  return Object.entries(values.fields).reduce<SaveSessionEndpointFieldValueType[]>((prev, [fieldId, fieldData]) => {
    const fieldValue = convertFieldToEndpointValue(fieldId, fieldData);
    return fieldValue ? [...prev, fieldValue] : prev;
  }, []);
}

function convertFieldToEndpointValue(
  fieldId: string,
  fieldData: SessionReportFormValuesFieldType
): SaveSessionEndpointFieldValueType | null {
  switch (fieldData.__typename) {
    case 'VOLUNTEER_BooleanFieldType':
      if (fieldData.value === '') return null;
      return {
        __typename: 'VOLUNTEER_BooleanFieldValue',
        fieldId,
        boolValue: fieldData.value === 'true',
      };
    case 'VOLUNTEER_CheckboxFieldType':
      return {
        __typename: 'VOLUNTEER_CheckboxFieldValue',
        fieldId,
        checked: fieldData.value,
      };
    case 'VOLUNTEER_DropdownFieldType':
      if (!fieldData.value || fieldData.value.length === 0) return null;
      return {
        __typename: 'VOLUNTEER_DropdownFieldValue',
        fieldId,
        dropdownFieldOptionIds: typeof fieldData.value === 'string' ? [fieldData.value] : fieldData.value,
      };
    case 'VOLUNTEER_NumericFieldType':
      if (fieldData.value === '') return null;
      return {
        __typename: 'VOLUNTEER_NumericFieldValue',
        fieldId,
        numericValue: parseFloat(fieldData.value),
      };
    case 'VOLUNTEER_RatingFieldType':
      if (fieldData.value === undefined) return null;
      return {
        __typename: 'VOLUNTEER_RatingFieldValue',
        fieldId,
        ratingValue: Number(fieldData.value),
      };
    case 'VOLUNTEER_TextFieldType':
      if (fieldData.value === '') return null;
      return {
        __typename: 'VOLUNTEER_TextFieldValue',
        fieldId,
        textValue: fieldData.value,
      };
    case 'VOLUNTEER_TimeFieldType':
      if (fieldData.value === '') return null;
      return {
        __typename: 'VOLUNTEER_TimeFieldValue',
        fieldId,
        timeValue: encodeTime(DateTime.fromJSDate(fieldData.value as Date)),
      };
    default:
      return null;
  }
}

